import { useEffect, useState } from "react";
import {
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Image as ImageIcon,
  X,
} from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import * as Switch from "@radix-ui/react-switch";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchBanners,
  updateBannerStatus,
} from "../../store/slices/bannersSlice";
import ReusableModal, { MODAL_TYPES } from "../../components/ReusableModal";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";
import Pagination from "../../components/tables/Pagination";

export default function ViewBannersPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Modal state management
  const [modalState, setModalState] = useState({
    open: false,
    type: null,
    data: null,
    loading: false,
  });

  // Image viewer modal state
  const [imageViewerState, setImageViewerState] = useState({
    open: false,
    banner: null,
  });

  const {
    banners = [],
    pagination,
    bannerChangeStatus,
    loading,
  } = useSelector((state) => state.banner);

  console.log("ViewBannersPage - banners from state:", banners);
  console.log("ViewBannersPage - banners length:", banners?.length);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const itemsPerPage = 10;

  // Calculate which backend page we need
  const backendPageSize = 100;
  const startRecord = (currentPage - 1) * itemsPerPage;
  const backendPage = Math.floor(startRecord / backendPageSize) + 1;

  useEffect(() => {
    dispatch(fetchBanners({ page: backendPage, per_page: backendPageSize }));
  }, [dispatch, backendPage]);

  const filteredBanners = banners.filter((banner) => {
    const matchesSearch =
      !searchTerm ||
      (banner.headline &&
        banner.headline.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (banner.tagline &&
        banner.tagline.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter =
      filterStatus === "all" ||
      (banner.is_enabled ? "enabled" : "disabled") === filterStatus;
    return matchesSearch && matchesFilter;
  });

  console.log("ViewBannersPage - filteredBanners:", filteredBanners);
  console.log("ViewBannersPage - searchTerm:", searchTerm);
  console.log("ViewBannersPage - filterStatus:", filterStatus);

  const hasActiveClientFilters = searchTerm !== "" || filterStatus !== "all";

  // Client-side pagination
  // Banners has no server-side pagination so always use filtered count
  const totalRecords = hasActiveClientFilters
    ? filteredBanners.length
    : (pagination?.total || filteredBanners.length);
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const backendStartIndex = (backendPage - 1) * backendPageSize;
  const localStartIndex = startRecord - backendStartIndex;
  const localEndIndex = localStartIndex + itemsPerPage;
  const paginatedBanners = filteredBanners.slice(localStartIndex, localEndIndex);

  const getStatusBadge = (status) => {
    const statusStyles = {
      enabled: "bg-green-100 text-green-800",
      disabled: "bg-red-100 text-red-800",
    };
    return statusStyles[status] || "bg-gray-100 text-gray-800";
  };

  const handlePageChange = (page) => {
    navigate(`?page=${page}`);
  };

  // Modal handlers
  const openModal = (type, data) => {
    setModalState({
      open: true,
      type,
      data: { ...data, entityType: "Banner" },
      loading: false,
    });
  };

  const closeModal = () => {
    setModalState({
      open: false,
      type: null,
      data: null,
      loading: false,
    });
  };

  const setModalLoading = (loading) => {
    setModalState((prev) => ({
      ...prev,
      loading,
    }));
  };

  const handleStatusChange = async (bannerData) => {
    setModalLoading(true);
    try {
      const result = await dispatch(
        updateBannerStatus({
          uuid: bannerData?.id,
          status: bannerData?.is_enabled ? "disable" : "enable",
        })
      );

      if (updateBannerStatus.fulfilled.match(result)) {
        closeModal();
      } else {
        setModalLoading(false);
      }
    } catch (error) {
      setModalLoading(false);
    }
  };

  const handleEditBanner = (banner) => {
    navigate(`/banners/add?edit=${banner.id}`);
  };

  const handleViewBanner = (banner) => {
    setImageViewerState({
      open: true,
      banner: banner,
    });
  };

  const closeImageViewer = () => {
    setImageViewerState({
      open: false,
      banner: null,
    });
  };

  const handleDeleteBanner = async (bannerData) => {
    setModalLoading(true);
    try {
      // TODO: Implement banner delete API call
      console.log("Deleting banner:", bannerData.id);
      // await dispatch(deleteBanner({ uuid: bannerData.uuid }));

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      showApiSuccess("Banner deleted successfully");
      closeModal();
    } catch (error) {
      setModalLoading(false);
      showApiError(null, "Failed to delete banner");
    }
  };

  // Function to trigger form submission from modal
  const handleModalConfirm = () => {
    if (modalState.type === MODAL_TYPES.ENABLE_DISABLE) {
      handleStatusChange(modalState.data);
    } else if (modalState.type === MODAL_TYPES.DELETE) {
      handleDeleteBanner(modalState.data);
    }
  };

  useEffect(() => {
    if (bannerChangeStatus === "success") {
      showApiSuccess("Status updated successfully");
    } else if (bannerChangeStatus === "failed") {
      showApiError(null, "Status update failed");
    }
  }, [bannerChangeStatus]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {loading && <Loader />}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">All Banners</h2>
          </div>
          <button
            onClick={() => navigate("/banners/add")}
            className="mt-4 sm:mt-0 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <ImageIcon className="h-4 w-4" />
            <span>Add New Banner</span>
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search banners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Banners Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Headline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tagline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Sequence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedBanners.length > 0 ? (
                paginatedBanners.map((banner) => (
                  <tr
                    key={banner.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div
                        className="w-20 h-12 rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleViewBanner(banner)}
                        title="Click to view full image"
                      >
                        {banner.image_url ? (
                          <img
                            src={banner.image_url}
                            alt="Banner"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {banner.headline || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {banner.tagline || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {banner.sequence !== null ? banner.sequence : "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(
                          banner.is_enabled ? "enabled" : "disabled"
                        )}`}
                      >
                        {banner.is_enabled ? "Enabled" : "Disabled"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center space-x-3">
                        <Switch.Root
                          id={`banner-status-${banner.id}`}
                          checked={banner?.is_enabled}
                          onCheckedChange={() =>
                            openModal(MODAL_TYPES.ENABLE_DISABLE, banner)
                          }
                          className="w-[42px] h-[25px] bg-gray-200 rounded-full relative shadow-sm 
                               data-[state=checked]:bg-blue-600 outline-none cursor-pointer"
                        >
                          <Switch.Thumb
                            className="block w-[21px] h-[21px] bg-white rounded-full transition-transform 
                                 translate-x-0.5 will-change-transform 
                                 data-[state=checked]:translate-x-[19px]"
                          />
                        </Switch.Root>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewBanner(banner)}
                            className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                            title="View Banner"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditBanner(banner)}
                            className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                            title="Edit Banner"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                         
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <ImageIcon className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    <p className="text-lg font-medium mb-2">No banners found</p>
                    <p className="text-sm">
                      {searchTerm || filterStatus !== "all"
                        ? "Try adjusting your search or filter criteria"
                        : "Get started by adding your first banner"}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalRecords}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}

      {/* Reusable Modal */}
      <ReusableModal
        open={modalState.open}
        onOpenChange={closeModal}
        type={modalState.type}
        data={modalState.data}
        onConfirm={handleModalConfirm}
        loading={modalState.loading}
        showActions={true}
      />

      {/* Image Viewer Modal */}
      <Dialog.Root open={imageViewerState.open} onOpenChange={closeImageViewer}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-4xl max-h-[95vh] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-lg focus:outline-none overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <ImageIcon className="h-5 w-5 text-gray-600" />
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Banner Details
                </Dialog.Title>
              </div>
              <Dialog.Close asChild>
                <button
                  className="rounded-md p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>

            {/* Content */}
            <div className="p-6">
              {imageViewerState.banner && (
                <div className="space-y-6">
                  {/* Banner Image */}
                  <div className="flex justify-center">
                    <div className="max-w-full max-h-[60vh] overflow-hidden rounded-lg border border-gray-200">
                      {imageViewerState.banner.image_url ? (
                        <img
                          src={imageViewerState.banner.image_url}
                          alt="Banner"
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <div className="w-64 h-32 flex items-center justify-center bg-gray-100">
                          <ImageIcon className="h-12 w-12 text-gray-400" />
                          <span className="ml-2 text-gray-500">No Image</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Banner Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Headline
                        </label>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                          {imageViewerState.banner.headline || "No headline"}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tagline
                        </label>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                          {imageViewerState.banner.tagline || "No tagline"}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Sequence
                        </label>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                          {imageViewerState.banner.sequence !== null
                            ? imageViewerState.banner.sequence
                            : "No sequence"}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(
                              imageViewerState.banner.is_enabled
                                ? "enabled"
                                : "disabled"
                            )}`}
                          >
                            {imageViewerState.banner.is_enabled
                              ? "Enabled"
                              : "Disabled"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        closeImageViewer();
                        handleEditBanner(imageViewerState.banner);
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
                    >
                      <Edit className="h-4 w-4" />
                      <span>Edit Banner</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
